import React, { useState } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { formatAmountWithCurrency } from "../../../utils/CurrencyUtils";

// Status colour scheme:
//   On track → green, Warranty → amber, Delayed → red, Completed → blue.
const ON_TRACK_COLOR = "#4BB34B";
const WARRANTY_COLOR = "#FFAD5C";
const DELAYED_COLOR = "#E54746";
const COMPLETED_COLOR = "#4D6BF6";

const Label = ({ children, align = "left" }) => (
    <Typography
        sx={{
            color: "#949DAA",
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: "Inter Tight",
            textAlign: align,
            whiteSpace: "nowrap",
        }}
    >
        {children}
    </Typography>
);

// One consistent size across every stat card — Total Projects, Total
// Customers, and each financial figure all read at the same size/height.
const Value = ({ children, align = "left" }) => (
    <Typography
        component="div"
        sx={{
            fontWeight: 600,
            fontSize: "20px",
            lineHeight: 1.2,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            textAlign: align,
            "& > span": {
                justifyContent:
                    align === "right"
                        ? "flex-end"
                        : align === "center"
                        ? "center"
                        : "flex-start",
            },
        }}
    >
        {children}
    </Typography>
);

// A segmented donut ring with a colour-dot legend. Each segment is
// proportional to its value, drawn with rounded caps and a small gap
// between segments (like the reference). Hovering a segment or a legend
// row surfaces that segment's value in the ring's centre. When every
// value is zero, only the light track shows.
const Donut = ({ items, size = 88, thickness = 8 }) => {
    const [hovered, setHovered] = useState(null);

    const total = items.reduce((s, it) => s + (it.value || 0), 0);
    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;
    const active = items.filter((it) => (it.value || 0) > 0);
    // Gap between segments (in path length). Only when there's more than
    // one segment to separate; skip when a single segment fills the ring.
    const gap = active.length > 1 ? 8 : 0;

    let offset = 0;
    const hoveredItem = hovered != null ? items[hovered] : null;

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px 24px",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: size,
                    height: size,
                    flexShrink: 0,
                }}
            >
                <svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    role="img"
                >
                    {/* Track */}
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke="#EEF0F3"
                        strokeWidth={thickness}
                    />
                    {total > 0 &&
                        items.map((it, i) => {
                            const value = it.value || 0;
                            if (!value) return null;
                            const full = (value / total) * circumference;
                            // Trim the drawn length so rounded caps + gap
                            // don't overlap the next segment.
                            const visible = Math.max(full - gap, 1);
                            const dashArray = `${visible} ${
                                circumference - visible
                            }`;
                            const dashOffset = -(offset + gap / 2);
                            offset += full;
                            const isHovered = hovered === i;
                            return (
                                <circle
                                    key={it.name}
                                    cx={center}
                                    cy={center}
                                    r={radius}
                                    fill="none"
                                    stroke={it.color}
                                    strokeWidth={thickness}
                                    strokeLinecap="round"
                                    strokeDasharray={dashArray}
                                    strokeDashoffset={dashOffset}
                                    transform={`rotate(-90 ${center} ${center})`}
                                    style={{
                                        cursor: "pointer",
                                        opacity:
                                            hovered == null || isHovered
                                                ? 1
                                                : 0.35,
                                        transition: "opacity 0.15s ease",
                                    }}
                                    onMouseEnter={() => setHovered(i)}
                                    onMouseLeave={() => setHovered(null)}
                                    onClick={() =>
                                        setHovered((prev) =>
                                            prev === i ? null : i,
                                        )
                                    }
                                >
                                    <title>{`${it.name}: ${value}`}</title>
                                </circle>
                            );
                        })}
                </svg>
                {/* Centre readout — segment value on hover, total otherwise. */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "Inter Tight",
                            fontWeight: 700,
                            fontSize: "20px",
                            lineHeight: 1,
                            color: hoveredItem ? hoveredItem.color : "#0D0D0E",
                        }}
                    >
                        {hoveredItem ? hoveredItem.value : total}
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Inter Tight",
                            fontWeight: 500,
                            fontSize: "10px",
                            color: "#949DAA",
                            marginTop: "2px",
                        }}
                    >
                        {hoveredItem ? hoveredItem.name : "Total"}
                    </Typography>
                </Box>
            </Box>

            {/* Legend — 2×2 grid (row 1: On track / Delayed, row 2:
                Warranty / Completed), matching the reference. */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, auto)",
                    columnGap: "24px",
                    rowGap: "12px",
                }}
            >
                {items.map((it, i) => (
                    <Box
                        key={it.name}
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered(null)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                        }}
                    >
                        <Box
                            sx={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                backgroundColor: it.color,
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            sx={{
                                fontFamily: "Inter Tight",
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#0D0D0E",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {it.name}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export default function ProjectStats({ stats, loading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    if (loading && !stats) {
        return (
            <Box
                sx={{
                    border: "1px solid #EEF0F3",
                    borderRadius: "12px",
                    height: isMobile ? 300 : 120,
                    backgroundColor: "#FBFCFE",
                }}
            />
        );
    }
    if (!stats) return null;

    const totalProjects = stats.total_project_count ?? 0;
    const customers = stats.total_customers ?? 0;

    const totalCost = stats.total_cost || 0;

    const onTrack = stats.on_track_projects || 0;
    const delayed = stats.delayed_projects || 0;
    const inWarranty = stats.in_warranty_projects || 0;
    const completed = stats.completed_projects || 0;

    // Reference layout: a flat row with a full-width bottom border, and a
    // short hairline between neighbouring sections that is bottom-aligned so
    // it runs down to touch that bottom border. Every block shares the same
    // UI: grey label on top, value underneath.
    const statBlock = (label, value) => (
        <Box
            className="stat-section"
            sx={{
                // Desktop: equal 1-share columns (donut takes 2 shares).
                // Mobile: two blocks per row.
                flex: isMobile ? "0 0 calc(50% - 8px)" : "1 1 0",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
            }}
        >
            {/* Label nudged down from the very top of the block. */}
            <Box sx={{ marginTop: "14px" }}>
                <Label>{label}</Label>
            </Box>
            {/* Value pinned to the bottom of the block. */}
            <Box sx={{ marginTop: "auto", paddingTop: "8px" }}>
                <Value>{value}</Value>
            </Box>
        </Box>
    );

    const statusSection = (
        <Box
            className="stat-section"
            sx={{
                // Two shares of the row on desktop, half-row on mobile.
                flex: isMobile ? "0 0 calc(50% - 8px)" : "2 1 0",
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Donut
                items={[
                    { name: "On track", value: onTrack, color: ON_TRACK_COLOR },
                    { name: "Delayed", value: delayed, color: DELAYED_COLOR },
                    {
                        name: "Warranty",
                        value: inWarranty,
                        color: WARRANTY_COLOR,
                    },
                    {
                        name: "Completed",
                        value: completed,
                        color: COMPLETED_COLOR,
                    },
                ]}
            />
        </Box>
    );

    // Short divider pinned to the bottom of the row so it touches the
    // container's bottom border, exactly like the reference.
    const vDivider = (
        <Box
            sx={{
                width: "1px",
                height: "32px",
                alignSelf: "flex-end",
                backgroundColor: "#EEF0F3",
            }}
        />
    );
    return (
        <Box
            sx={{
                borderBottom: "1px solid #EEF0F3",
                backgroundColor: "transparent",
                paddingTop: isMobile ? "4px" : "6px",
                paddingBottom: 0,
                display: "flex",
                flexDirection: "row",
                // Mobile: wrap into two blocks per row (no dividers).
                flexWrap: isMobile ? "wrap" : "nowrap",
                alignItems: "stretch",
                gap: "16px",
                "& > .stat-section": {
                    paddingBottom: isMobile ? "16px" : "20px",
                },
            }}
        >
            {statBlock("Total Cost", formatAmountWithCurrency(totalCost))}
            {!isMobile && vDivider}
            {statBlock("Total Customers", customers)}
            {!isMobile && vDivider}
            {statBlock("Total Projects", totalProjects)}
            {!isMobile && vDivider}
            {statusSection}
        </Box>
    );
}
